import { performJsonRequest, } from "./core.js";
const buildListQuery = (params) => {
    if (!params) {
        return undefined;
    }
    const query = {};
    if (params.status) {
        query.status = params.status;
    }
    if (params.state) {
        query.state = params.state;
    }
    if (params.venueId) {
        query["venue_id"] = params.venueId;
    }
    if (params.search) {
        query.search = params.search;
    }
    if (params.startsFrom) {
        query["starts_from"] = params.startsFrom;
    }
    if (params.startsTo) {
        query["starts_to"] = params.startsTo;
    }
    if (params.orderBy) {
        query["order_by"] = params.orderBy;
    }
    if (params.page !== undefined) {
        query.page = params.page;
    }
    if (params.pageSize !== undefined) {
        query["page_size"] = params.pageSize;
    }
    return Object.keys(query).length > 0 ? query : undefined;
};
export const normalizeEventsServiceUrl = (url) => {
    const trimmed = url.trim();
    if (trimmed.length === 0) {
        return trimmed;
    }
    const withoutTrailingSlash = trimmed.replace(/\/+$/, "");
    // Only strip trailing /events from absolute backend URLs (e.g. http://events:8080/events).
    // Never strip from relative BFF proxy paths like /api/bff/events.
    if (withoutTrailingSlash.endsWith("/events") &&
        /^https?:\/\//.test(withoutTrailingSlash)) {
        return withoutTrailingSlash.slice(0, -"/events".length);
    }
    return withoutTrailingSlash;
};
export const isAssetURLExpired = (expiresAt) => {
    try {
        const expiry = new Date(expiresAt);
        return expiry <= new Date();
    }
    catch {
        return true; // If we can't parse the date, consider it expired
    }
};
export const needsAssetURLRefresh = (asset) => {
    return !asset.url || isAssetURLExpired(asset.expires_at || "");
};
/**
 * Creates a new client for the public Events service.
 * @param config - The HTTP client configuration, including the base URL.
 * @returns A new EventsClient instance.
 */
export const createEventsClient = (config) => {
    const normalizedBaseUrl = normalizeEventsServiceUrl(config.baseUrl);
    const normalizedConfig = normalizedBaseUrl === config.baseUrl
        ? config
        : {
            ...config,
            baseUrl: normalizedBaseUrl,
        };
    return {
        listEvents: (params) => performJsonRequest(normalizedConfig, {
            method: "GET",
            path: "/events",
            context: params?.context,
            query: buildListQuery(params),
        }),
        getEvent: (params) => performJsonRequest(normalizedConfig, {
            method: "GET",
            path: `/events/${params.id}`,
            context: params.context,
        }),
        resolveEventRoute: (params) => performJsonRequest(normalizedConfig, {
            method: "GET",
            path: `/public/event-routes/${encodeURIComponent(params.routeKey)}`,
            context: params.context,
        }),
        fetchAssetURL: (params) => performJsonRequest(normalizedConfig, {
            method: "GET",
            path: `/events/${params.eventId}/assets/${params.assetId}/url`,
            context: params.context,
        }),
        fetchEventProvinces: (context) => performJsonRequest(normalizedConfig, {
            method: "GET",
            path: "/events/meta/provinces",
            context,
        }),
    };
};
const encode = (value) => encodeURIComponent(value);
/**
 * Creates a new client for the admin-only Events service.
 * @param config - The HTTP client configuration, including the base URL.
 * @returns A new AdminEventsClient instance.
 */
export const createAdminEventsClient = (config) => {
    const normalizedBaseUrl = normalizeEventsServiceUrl(config.baseUrl);
    const normalizedConfig = normalizedBaseUrl === config.baseUrl
        ? config
        : {
            ...config,
            baseUrl: normalizedBaseUrl,
        };
    return {
        listEvents: (params) => performJsonRequest(normalizedConfig, {
            method: "GET",
            path: "/admin/events",
            context: params?.context,
        }),
        getEvent: (params) => performJsonRequest(normalizedConfig, {
            method: "GET",
            path: `/admin/events/${encode(params.eventId)}`,
            context: params.context,
        }),
        createEvent: (params) => performJsonRequest(normalizedConfig, {
            method: "POST",
            path: "/admin/events",
            context: params.context,
            body: params.body,
            includeIdempotencyKey: true,
            headers: params.headers,
        }),
        updateEvent: (params) => performJsonRequest(normalizedConfig, {
            method: "PUT",
            path: `/admin/events/${encode(params.eventId)}`,
            context: params.context,
            body: params.body,
            includeIdempotencyKey: true,
            headers: params.headers,
        }),
        deleteEvent: (params) => performJsonRequest(normalizedConfig, {
            method: "DELETE",
            path: `/admin/events/${encode(params.eventId)}`,
            context: params.context,
            includeIdempotencyKey: true,
        }),
        discardEvent: (params) => performJsonRequest(normalizedConfig, {
            method: "POST",
            path: `/admin/events/${encode(params.eventId)}/discard`,
            context: params.context,
            includeIdempotencyKey: true,
        }),
        cancelEvent: (params) => performJsonRequest(normalizedConfig, {
            method: "POST",
            path: `/admin/events/${encode(params.eventId)}/cancel`,
            context: params.context,
            includeIdempotencyKey: true,
        }),
        publishEvent: (params) => performJsonRequest(normalizedConfig, {
            method: "POST",
            path: `/admin/events/${encode(params.eventId)}/publish`,
            context: params.context,
            includeIdempotencyKey: true,
        }),
        fetchAssetURL: (params) => performJsonRequest(normalizedConfig, {
            method: "GET",
            path: `/events/${params.eventId}/assets/${params.assetId}/url`,
            context: params.context,
        }),
        updateTicketType: (params) => performJsonRequest(normalizedConfig, {
            method: "PUT",
            path: `/admin/ticket-types/${encode(params.ticketTypeId)}`,
            context: params.context,
            body: params.body,
        }),
        getTicketTypeDefaults: () => performJsonRequest(normalizedConfig, {
            method: "GET",
            path: "/admin/ticket-type-defaults",
        }),
    };
};
